import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet } from 'react-router-dom';
import { Dialog, Transition, Popover } from '@headlessui/react';
import {
	ArrowLeftEndOnRectangleIcon,
	Bars3Icon,
	UserIcon,
	XMarkIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	ChartPieIcon,
	DocumentChartBarIcon,
	UsersIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../../api/AuthContext';
import useUserContext from '../../api/UserContext';
import Logo from '../../components/Logo';
import routes from '../../config/routes';
import type { SideBarNavigationProps } from './sidebar/types';

function classNames(...classes: string[]) {
	return classes.filter(Boolean).join(' ');
}

// Reports configuration for admin users
const adminReports = [
	{
		name: 'User Report',
		description: 'View detailed user analytics and membership data',
		href: routes.ADMIN_LINKS.USER_REPORTS,
		icon: UsersIcon
	},
	{
		name: 'Category Breakdown',
		description: 'Analyze question distribution by categories',
		href: routes.ADMIN_LINKS.CATEGORY_BREAKDOWN,
		icon: ChartPieIcon
	},
	{
		name: 'Diagnostic Tests Report',
		description: 'Track performance on diagnostic evaluations',
		href: routes.ADMIN_LINKS.REPORT_PRUEBAS_DIAGNOSTICAS,
		icon: DocumentChartBarIcon
	}
];

const SideBarLayout = ({
	currentPageName,
	handleNavigation,
	navigation,
	otherLinks
}: SideBarNavigationProps) => {
	// const location = useLocation();
	const { currentUser } = useUserContext();
	const { signOutCurrentUser } = useAuthContext();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [mobileReportsExpanded, setMobileReportsExpanded] = useState(false);

	useEffect(() => {
		if (currentUser !== null) {
			setIsAdmin(currentUser?.isAdmin ?? false);
		}
	}, [currentUser]);

	const signOut = () => {
		signOutCurrentUser()
			.then(() => {
				console.log('User signed out successfully');
			})
			.catch((error) => {
				console.error('Error signing out: ', error);
			});
	};

	return (
		<>
			<Transition.Root show={sidebarOpen} as={Fragment}>
				<Dialog
					as='div'
					className='relative z-50 lg:hidden'
					onClose={setSidebarOpen}>
					<Transition.Child
						as={Fragment}
						enter='transition-opacity ease-linear duration-300'
						enterFrom='opacity-0'
						enterTo='opacity-100'
						leave='transition-opacity ease-linear duration-300'
						leaveFrom='opacity-100'
						leaveTo='opacity-0'>
						<div className='fixed inset-0 bg-gray-900/80' />
					</Transition.Child>

					<div className='fixed inset-0 flex'>
						<Transition.Child
							as={Fragment}
							enter='transition ease-in-out duration-300 transform'
							enterFrom='-translate-x-full'
							enterTo='translate-x-0'
							leave='transition ease-in-out duration-300 transform'
							leaveFrom='translate-x-0'
							leaveTo='-translate-x-full'>
							<Dialog.Panel className='relative mr-16 flex w-full max-w-xs flex-1'>
								<Transition.Child
									as={Fragment}
									enter='ease-in-out duration-300'
									enterFrom='opacity-0'
									enterTo='opacity-100'
									leave='ease-in-out duration-300'
									leaveFrom='opacity-100'
									leaveTo='opacity-0'>
									<div className='absolute left-full top-0 flex w-16 justify-center pt-5'>
										<button
											type='button'
											className='-m-2.5 p-2.5'
											onClick={() => setSidebarOpen(false)}>
											<span className='sr-only'>Close sidebar</span>
											<XMarkIcon
												className='h-6 w-6 text-white'
												aria-hidden='true'
											/>
										</button>
									</div>
								</Transition.Child>
								{/* Sidebar component, swap this element with another sidebar if you like */}
								<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-brand-800 px-6 pb-2'>
									<div className='flex h-16 shrink-0 items-center'>
										{/* <img
											className='h-8 w-auto'
											src='https://tailwindui.com/img/logos/mark.svg?color=white'
											alt='Your Company'
										/> */}
										<Logo />
									</div>
									{/* SIDE BAR NAV */}
									<nav className='flex flex-1 flex-col'>
										<ul className='flex flex-1 flex-col gap-y-7'>
											{/* MAIN NAV */}
											<li>
												<ul className='-mx-2 space-y-1'>
													{navigation
														.filter((n) => {
															if (
																currentUser?.examTarget !== 'enurm' &&
																n.href === routes.APP.ENURM.PRUEBAS_DIAGNOSTICAS
															) {
																return false;
															} else {
																return true;
															}
														})
														.map((link) => (
															<li key={link.name}>
																<Link
																	to={link.href}
																	onClick={() => handleNavigation(link.name)}
																	className={classNames(
																		link.current
																			? 'bg-brand-700 text-brand-25'
																			: 'text-brand-50 ',
																		'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-brand-700'
																	)}>
																	<link.icon
																		className={classNames(
																			link.current
																				? 'text-brand-100'
																				: 'text-brand-200 group-',
																			'h-6 w-6 shrink-0'
																		)}
																		aria-hidden='true'
																	/>
																	{link.name}
																</Link>
															</li>
														))}
												</ul>
											</li>
											{/* OTROS */}
											<li>
												<div className='text-xs font-semibold leading-6 text-brand-200'>
													Otros
												</div>
												<ul className='-mx-2 mt-2 space-y-1'>
													{otherLinks.map((link) => (
														<li key={link.name}>
															<Link
																to={link.href}
																onClick={() => handleNavigation(link.name)}
																className={classNames(
																	link.current
																		? 'bg-sky-700 text-brand-25'
																		: 'text-brand-50 ',
																	'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'
																)}>
																<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-white'>
																	{link.initial}
																</span>
																<span className='truncate'>{link.name}</span>
															</Link>
														</li>
													))}
												</ul>
											</li>
											{isAdmin && (
												<li>
													<div className='text-xs font-semibold leading-6 text-brand-200'>
														Admin Links
													</div>
													<ul className='-mx-2 mt-2 space-y-1'>
														{/* Questions Manager */}
														<li>
															<Link
																to={routes.ADMIN_LINKS.QUESTIONS_MANAGER}
																className='text-brand-100 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'>
																<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-white'>
																	Q
																</span>
																<span className='truncate'>Questions Mgmt</span>
															</Link>
														</li>

														{/* Reports Section - Mobile Collapsible */}
														<li>
															<button
																onClick={() =>
																	setMobileReportsExpanded(
																		!mobileReportsExpanded
																	)
																}
																className='text-brand-100 group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700 items-center'>
																<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-white'>
																	R
																</span>
																<span className='truncate flex-1 text-left'>
																	Reports
																</span>
																{mobileReportsExpanded ? (
																	<ChevronDownIcon className='h-4 w-4 text-brand-200' />
																) : (
																	<ChevronRightIcon className='h-4 w-4 text-brand-200' />
																)}
															</button>

															{/* Mobile Reports Submenu - Inline */}
															{mobileReportsExpanded && (
																<ul className='mt-2 space-y-1 ml-6'>
																	{adminReports.map((report) => (
																		<li key={report.name}>
																			<Link
																				to={report.href}
																				className='text-brand-200 group flex gap-x-3 rounded-md p-2 text-xs leading-5 font-medium hover:bg-sky-700 hover:text-brand-100'>
																				<report.icon className='h-4 w-4 shrink-0' />
																				<div className='flex flex-col'>
																					<span className='truncate'>
																						{report.name}
																					</span>
																					<span className='text-[0.6rem] text-brand-300 truncate'>
																						{report.description}
																					</span>
																				</div>
																			</Link>
																		</li>
																	))}
																</ul>
															)}
														</li>
													</ul>
												</li>
											)}
											<li className='mt-auto'>
												<Link
													to={routes.HOME}
													onClick={() => signOut()}
													className={classNames(
														'text-brand-50 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'
													)}>
													<ArrowLeftEndOnRectangleIcon
														className='h-6 w-6 shrink-0 text-brand-50 group- hover:bg-sky-700'
														aria-hidden='true'
													/>
													Cerrar Sesión
												</Link>
											</li>
										</ul>
									</nav>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
			{/* // TODO: Add a transition for the sidebar
			// TODO: Separate the mobile nav and statis sidebar into components */}
			{/* Static sidebar for desktop */}
			{/* lg:w-72 */}
			<div className='hidden lg:fixed lg:inset-y-0 lg:z-[60] lg:flex lg:flex-col xl:w-72'>
				{/* Sidebar component, swap this element with another sidebar if you like */}
				<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-sky-800 px-6'>
					<div className='flex h-16 shrink-0 items-center mt-8'>
						<Logo />
					</div>
					<nav className='flex flex-1 flex-col'>
						<ul className='flex flex-1 flex-col gap-y-7'>
							{/* MAIN NAV */}
							<li>
								<ul className='-mx-2 space-y-1'>
									{navigation
										.filter((n) => {
											if (
												currentUser?.examTarget !== 'enurm' &&
												n.href === routes.APP.ENURM.PRUEBAS_DIAGNOSTICAS
											) {
												return false;
											} else {
												return true;
											}
										})
										.map((link) => (
											<li key={link.name}>
												<Link
													to={link.href}
													onClick={() => handleNavigation(link.name)}
													className={classNames(
														link.current
															? 'bg-sky-700 text-brand-25'
															: 'text-brand-50 ',
														'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'
													)}>
													<link.icon
														className={classNames(
															link.current
																? 'text-brand-100'
																: 'text-brand-200 group-',
															'h-6 w-6 shrink-0'
														)}
														aria-hidden='true'
													/>
													{link.name}
												</Link>
											</li>
										))}
								</ul>
							</li>

							{/* OTROS */}
							<li>
								<div className='text-xs font-semibold leading-6 text-brand-200'>
									Otros enlaces
								</div>
								<ul className='-mx-2 mt-2 space-y-1'>
									{otherLinks.map((link) => (
										<li key={link.name}>
											<Link
												to={link.href}
												onClick={() => handleNavigation(link.name)}
												className={classNames(
													link.current
														? 'bg-sky-700 text-brand-25'
														: 'text-brand-50   ',
													'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'
												)}>
												<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-brand-25'>
													{link.initial}
												</span>
												<span className='truncate'>{link.name}</span>
											</Link>
										</li>
									))}
								</ul>
							</li>
							{isAdmin && (
								<li>
									<div className='text-xs font-semibold leading-6 text-brand-200'>
										ADMIN LINKS
									</div>
									<ul className='-mx-2 mt-2 space-y-1'>
										{/* Questions Manager */}
										<li>
											<Link
												to={routes.ADMIN_LINKS.QUESTIONS_MANAGER}
												className='text-brand-200 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700'>
												<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-white'>
													Q
												</span>
												<span className='truncate'>Questions Mgmt</span>
											</Link>
										</li>

										{/* Reports Section - Floating Popover */}
										<li>
											<Popover className='relative z-[9998]'>
												<Popover.Button className='text-brand-200 group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold hover:bg-sky-700 items-center'>
													<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-brand-400 bg-brand-500 text-[0.625rem] font-medium text-white'>
														R
													</span>
													<span className='truncate flex-1 text-left'>
														Reports
													</span>
													<ChevronRightIcon className='h-4 w-4 text-brand-200' />
												</Popover.Button>

												<Transition
													as={Fragment}
													enter='transition ease-out duration-200'
													enterFrom='opacity-0 translate-y-1'
													enterTo='opacity-100 translate-y-0'
													leave='transition ease-in duration-150'
													leaveFrom='opacity-100 translate-y-0'
													leaveTo='opacity-0 translate-y-1'>
													<Popover.Panel>
														{({ close }) =>
															createPortal(
																<div className='fixed inset-0 z-[9999] flex items-start justify-start'>
																	<div
																		className='fixed inset-0 bg-black bg-opacity-25'
																		onClick={() => close()}
																	/>
																	<div
																		className='relative ml-[18rem] mt-[12rem] w-screen max-w-md'
																		style={{
																			transform: 'translateX(1rem)'
																		}}>
																		<div className='overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5'>
																			<div className='p-4'>
																				<div className='grid gap-4'>
																					{adminReports.map((report) => (
																						<Link
																							key={report.name}
																							to={report.href}
																							className='group relative flex gap-x-6 rounded-lg p-4 hover:bg-gray-100'
																							onClick={() => close()}>
																							<div className='flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white'>
																								<report.icon className='h-6 w-6 text-gray-600 group-hover:text-brand-600' />
																							</div>
																							<div>
																								<span className='font-semibold text-gray-900'>
																									{report.name}
																									<span className='absolute inset-0' />
																								</span>
																								<p className='mt-1 text-gray-600 text-sm mb-0'>
																									{report.description}
																								</p>
																							</div>
																						</Link>
																					))}
																				</div>
																			</div>
																			<div className='bg-gray-50 px-8 py-6'>
																				<div className='flex items-center gap-x-3'>
																					<h3 className='text-sm font-semibold text-gray-900'>
																						Admin Tools
																					</h3>
																					<p className='rounded-full bg-brand-600/10 px-2.5 py-1.5 text-xs font-semibold text-brand-600'>
																						New
																					</p>
																				</div>
																				<p className='mt-2 text-sm text-gray-600'>
																					Advanced reporting and analytics for
																					administrators.
																				</p>
																			</div>
																		</div>
																	</div>
																</div>,
																document.body
															)
														}
													</Popover.Panel>
												</Transition>
											</Popover>
										</li>
									</ul>
								</li>
							)}
							<li className='-mx-6 mt-auto'>
								<Link
									to={routes.APP.USER_PROFILE}
									className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-brand-200 hover:bg-sky-700'>
									{/* <img
										className='h-8 w-8 rounded-full bg-brand-750'
										src='https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
										alt=''
									/> */}
									<UserIcon className='h-6 w-6' aria-hidden='true' />
									<span className='sr-only text-brand-25'>Tu perfil</span>
									<span aria-hidden='true' className='text-brand-25'>
										{currentUser?.person?.firstName}{' '}
										{currentUser?.person?.lastName}
									</span>
								</Link>
								<Link
									to={routes.HOME}
									onClick={() => signOut()}
									className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-brand-200 hover:bg-sky-700'>
									<ArrowLeftEndOnRectangleIcon
										className='h-6 w-6'
										aria-hidden='true'
									/>
									<span className='text-brand-25'>Cerrar sesión</span>
								</Link>
							</li>
						</ul>
					</nav>
				</div>
			</div>
			<div className='sticky top-0 z-40 flex items-center gap-x-6 bg-brand-700 px-4 py-4 shadow-sm sm:px-6 lg:hidden'>
				<button
					type='button'
					className='-m-2.5 p-2.5 text-brand-200 lg:hidden'
					onClick={() => setSidebarOpen(true)}>
					<span className='sr-only'>Open sidebar</span>
					<Bars3Icon className='h-6 w-6' aria-hidden='true' />
				</button>
				<div className='flex-1 text-sm font-semibold leading-6 text-white'>
					{currentPageName}
				</div>
				<Link to={routes.APP.USER_PROFILE}>
					<span className='sr-only'>Your profile</span>
					{/* <img
						className='h-8 w-8 rounded-full bg-brand-750'
						src='https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
						alt=''
					/> */}
					<UserIcon className='h-6 w-6 text-white' aria-hidden='true' />
				</Link>
			</div>
			<Outlet />
		</>
	);
};
export default SideBarLayout;
